import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api/client';
import { getActiveTenantId } from '@/lib/tenant';
import type { EmailThread, OutboundEmail, UpdateOutboundEmailInput } from '@/lib/api/types';

// ----- Agent outbound email (spec 0008, AC-8) -----

const emailKeys = {
  drafts: (agentId: string | undefined) =>
    ['email', getActiveTenantId(), 'drafts', agentId] as const,
  thread: (agentId: string | undefined, sessionId: string | undefined) =>
    ['email', getActiveTenantId(), 'thread', agentId, sessionId] as const,
};

/**
 * A draft mutation (edit, approve, discard) can change the drafts list, the
 * thread interleave, and the inbox rows (their has_outbound flag), so all
 * three domains are invalidated together.
 */
function invalidateEmailViews(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: ['email'] });
  void queryClient.invalidateQueries({ queryKey: ['agent-email'] });
}

/** Pending drafts for an agent, newest first. */
export function useAgentEmailDrafts(agentId: string | undefined) {
  return useQuery({
    queryKey: emailKeys.drafts(agentId),
    queryFn: () => apiFetch<OutboundEmail[]>(`/agents/${agentId}/email/drafts`),
    enabled: Boolean(agentId),
  });
}

/** Interleaved inbound/outbound entries of one email conversation. */
export function useEmailThread(agentId: string | undefined, sessionId: string | undefined) {
  return useQuery({
    queryKey: emailKeys.thread(agentId, sessionId),
    queryFn: () => apiFetch<EmailThread>(`/agents/${agentId}/email/threads/${sessionId}`),
    enabled: Boolean(agentId) && Boolean(sessionId),
  });
}

/** Edit a pending draft's subject/body (409 once it is no longer pending). */
export function useUpdateOutboundEmail() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateOutboundEmailInput }) =>
      apiFetch<OutboundEmail>(`/email/outbound/${id}`, { method: 'PATCH', body: input }),
    onSuccess: () => invalidateEmailViews(queryClient),
  });
}

/** Approve a pending draft: the send happens now with the current content. */
export function useApproveOutboundEmail() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<OutboundEmail>(`/email/outbound/${id}/approve`, { method: 'POST' }),
    onSuccess: () => invalidateEmailViews(queryClient),
  });
}

/** Discard a pending draft; it is never sent. */
export function useDiscardOutboundEmail() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<OutboundEmail>(`/email/outbound/${id}/discard`, { method: 'POST' }),
    onSuccess: () => invalidateEmailViews(queryClient),
  });
}
